// NavBar — right-anchored dark editorial nav.
// Self-contained: ember-nebula WebGL backdrop, 6-city world clock, user
// footer with optional logout. SPA-friendly: navItems carry { key, label,
// icon } and clicks invoke onNavigate(key) instead of href hops.
//
// Wordmark intentionally omitted in the moreece port — Cmd+M opens the
// assistant chat (the "Ask moreece" affordance the old logo held).
import { useEffect, useRef } from 'react';
import { ShaderRunner } from './shaderRunner';
import { EMBER_NEBULA } from './emberShader';
import { WorldClock } from './WorldClock';
import SystemStatus from '../../lib/SystemStatus';
import './navbar.css';

const NAV_ITEMS_DEFAULT = [
  { key: 'home',     label: 'Home',     icon: 'home' },
  { key: 'meetings', label: 'Meetings', icon: 'signals' },
  { key: 'calendar', label: 'Calendar', icon: 'calendar' },
  { key: 'tasks',    label: 'Tasks',    icon: 'tasks' },
  { key: 'sales',    label: 'Sales',    icon: 'reports' },
  { key: 'creative', label: 'Creative', icon: 'creative' },
  { key: 'stock',    label: 'Stock',    icon: 'stock' },
  { key: 'finance',  label: 'Finance',  icon: 'finance' },
  { key: 'contacts', label: 'Contacts', icon: 'team' },
  { key: 'settings', label: 'Settings', icon: 'settings' },
];

const ICON_PATHS = {
  home:     'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-9z',
  calendar: 'M4 7h16M6 4v3M18 4v3M4 10h16v10H4z',
  signals:  'M4 12h3l2-6 4 12 2-6 2 3h3',
  tasks:    'M4 6h12M4 12h12M4 18h8',
  reports:  'M4 20V8m6 12V4m6 16v-8',
  creative: 'M12 2l2.5 6 6.5.5-5 4.5 1.5 6.5L12 16l-5.5 3.5L8 13 3 8.5 9.5 8z',
  stock:    'M4 7l8-4 8 4v10l-8 4-8-4V7zM4 7l8 4 8-4M12 11v10',
  finance:  'M3 6h18M3 12h18M3 18h18',
  team:     'M8 11a3 3 0 100-6 3 3 0 000 6zm8 0a3 3 0 100-6 3 3 0 000 6zM3 20v-1a4 4 0 014-4h2a4 4 0 014 4v1M14 20v-1a4 4 0 014-4h0a3 3 0 013 3v2',
  settings: 'M12 9a3 3 0 100 6 3 3 0 000-6zm7 3l2 1-1 3-2-1a7 7 0 01-2 2l1 2-3 1-1-2a7 7 0 01-2 0l-1 2-3-1 1-2a7 7 0 01-2-2l-2 1-1-3 2-1a7 7 0 010-2l-2-1 1-3 2 1a7 7 0 012-2l-1-2 3-1 1 2a7 7 0 012 0l1-2 3 1-1 2a7 7 0 012 2l2-1 1 3-2 1a7 7 0 010 2z',
  // Cases — briefcase / portfolio (top-bar, body, divider, handle).
  cases:    'M3 8h18v12H3zM9 8V5a2 2 0 012-2h2a2 2 0 012 2v3M3 13h18',
  // Clouseau — two eyes (outer rings + pupils). Pass-3 swaps in cursor-tracking version.
  eyes:     'M9 12a3 3 0 106 0 3 3 0 10-6 0M2 12a7 7 0 1014 0 7 7 0 10-14 0M22 12a7 7 0 11-14 0 7 7 0 0114 0',
  // Vault — padlock (shackle arc + body).
  vault:    'M7 10V7a5 5 0 0110 0v3M5 10h14v11H5z',
};

function NavIcon({ kind }) {
  const path = ICON_PATHS[kind];
  if (!path) return null;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'><path d='${path}'/></svg>`;
  const url = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  return (
    <span
      className="nb-ico"
      style={{
        WebkitMaskImage: url, maskImage: url,
        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center', maskPosition: 'center',
        WebkitMaskSize: 'contain', maskSize: 'contain',
        background: 'currentColor',
      }}
    />
  );
}

export function NavBar({
  user = { name: 'Mo', role: 'Founder', hue: 158 },
  active = 'home',
  nebula = true,
  navItems = NAV_ITEMS_DEFAULT,
  onNavigate,
  onUserClick,
  onLogout,
  onSignalsClick, // admin-only — opens System Signals control panel
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !nebula) return;
    let runner;
    try {
      runner = new ShaderRunner(canvasRef.current, EMBER_NEBULA, { autoDrift: true });
    } catch (e) {
      console.warn('[NavBar] shader init failed, falling back to static', e);
      return;
    }
    return () => runner && runner.destroy();
  }, [nebula]);

  return (
    <aside className="nb-root" data-screen-label="Sidebar Nav">
      {nebula && <canvas ref={canvasRef} className="nb-canvas" aria-hidden="true" />}

      <div className="nb-inner">
        <nav className="nb-nav" aria-label="Primary">
          {navItems.map(item => (
            <button
              type="button"
              key={item.key}
              onClick={() => onNavigate?.(item.key)}
              className={
                'nb-link'
                + (item.key === active ? ' is-active' : '')
                // `graduated` pages stay permission-gated (item.wip) but render
                // like any live page — no red "in development" treatment.
                + (item.wip && !item.graduated ? ' is-wip' : '')
              }
              aria-current={item.key === active ? 'page' : undefined}
              title={item.wip && !item.graduated ? `${item.label} — still in development` : undefined}
            >
              <NavIcon kind={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* nb-spacer used to push these to the bottom; now obsolete because
            .nb-nav is flex:1 with overflow-y:auto so it absorbs all the
            free vertical space and these always sit pinned at the bottom. */}
        <WorldClock />

        <div className="nb-foot">
          {/* The user-avatar slot is now an Apple-style system status dot:
              ambient, calm, no clutter. The user's name/role still sits
              alongside it; the logout button is unchanged. */}
          <div className="nb-avatar nb-avatar-status" aria-hidden="true">
            <SystemStatus />
          </div>
          <div
            className={'nb-who' + (onUserClick ? ' is-clickable' : '')}
            onClick={onUserClick}
            role={onUserClick ? 'button' : undefined}
          >
            <span className="nb-name">{user.name}</span>
          </div>
          {onSignalsClick && (
            <button
              className="nb-signals"
              onClick={onSignalsClick}
              aria-label="System signals"
              title="System signals — data freshness + ingestion health"
              type="button"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                {/* concentric pulse / radar */}
                <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
                <path d="M8.5 8.5a5 5 0 0 1 7 7" />
                <path d="M5 5a10 10 0 0 1 14 14" />
              </svg>
            </button>
          )}
          {onLogout && (
            <button className="nb-logout" onClick={onLogout} aria-label="Log out" type="button">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
